/**
 * Phase 11-E — billing / plan panel (Professional upgrade via Stripe).
 */
import {
	Alert,
	Box,
	Button,
	Chip,
	Stack,
	Typography,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
	OrganizationRole,
	type OrgUsageSummaryDTO,
	SubscriptionTier,
} from "@flaha/shared-types";

import { useAuth } from "../../auth";
import { PlanTierCards } from "../pricing/PlanTierCards";
import { GlassSurface } from "../../layouts/components/GlassSurface";
import { getApiClient } from "../../services/apiClientProvider";

export function BillingPanel() {
	const { activeOrganization, memberships } = useAuth();
	const [searchParams] = useSearchParams();
	const [usage, setUsage] = useState<OrgUsageSummaryDTO | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	const role =
		memberships.find((m) => m.organizationId === activeOrganization?.id)?.role ??
		null;
	const isOwner = role === OrganizationRole.OWNER;
	const checkoutFlash = searchParams.get("checkout");

	useEffect(() => {
		let cancelled = false;
		void getApiClient()
			.getMyUsage()
			.then((res) => {
				if (!cancelled) setUsage(res.usage);
			})
			.catch((err: unknown) => {
				if (!cancelled) {
					setError(err instanceof Error ? err.message : String(err));
				}
			});
		return () => {
			cancelled = true;
		};
	}, [activeOrganization?.id]);

	const startCheckout = async () => {
		setBusy(true);
		setError(null);
		try {
			const res = await getApiClient().createProfessionalCheckout();
			window.location.assign(res.url);
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
			setBusy(false);
		}
	};

	const openPortal = async () => {
		setBusy(true);
		setError(null);
		try {
			const res = await getApiClient().createBillingPortal();
			window.location.assign(res.url);
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
			setBusy(false);
		}
	};

	const tier = usage?.entitlements.tier ?? SubscriptionTier.FREE;
	const isPro =
		tier === SubscriptionTier.PROFESSIONAL ||
		tier === SubscriptionTier.ENTERPRISE;

	return (
		<Box data-testid="account-billing-panel">
			{checkoutFlash === "success" && (
				<Alert severity="success" sx={{ mb: 2 }}>
					Checkout completed. Plan updates apply after Stripe confirms payment
					(usually within a few seconds). Refresh if usage still shows Free.
				</Alert>
			)}
			{checkoutFlash === "canceled" && (
				<Alert severity="info" sx={{ mb: 2 }}>
					Checkout canceled. Your plan was not changed.
				</Alert>
			)}
			{error && (
				<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
					{error}
				</Alert>
			)}

			{/* Always show the three tiers so owners see what they can unlock. */}
			<Box sx={{ mb: 3 }}>
				<PlanTierCards dense headingId="billing-plans-title" />
			</Box>

			<GlassSurface intensity="subtle" depth="flat" sx={{ p: 2.5 }}>
				<Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
					<CreditCardIcon sx={{ color: "workspace.textMuted" }} aria-hidden />
					<Typography variant="subtitle1" fontWeight={700}>
						Plan & billing
					</Typography>
					{usage && (
						<Chip
							size="small"
							label={
								usage.entitlements.grandfathered
									? `${tier} (demo)`
									: tier
							}
							color={isPro ? "success" : "default"}
							variant="outlined"
						/>
					)}
				</Stack>

				{usage?.entitlements.maxLifetimeTests != null && (
					<Typography variant="body2" sx={{ color: "workspace.textSecondary", mb: 1 }}>
						Lifetime tests: {usage.lifetimeTestsUsed} of{" "}
						{usage.entitlements.maxLifetimeTests}
					</Typography>
				)}
				{usage?.entitlements.maxTestsPerBillingPeriod != null && (
					<Typography variant="body2" sx={{ color: "workspace.textSecondary", mb: 1 }}>
						This period: {usage.periodTestsUsed} of{" "}
						{usage.entitlements.maxTestsPerBillingPeriod} tests
					</Typography>
				)}

				<Typography variant="body2" sx={{ color: "workspace.textSecondary", mb: 2 }}>
					Professional ($40/month) unlocks projects, Moderate tests, standard PDF
					reports, and up to three organization seats. Only the organization owner
					can change billing.
				</Typography>

				{!isOwner && (
					<Alert severity="info">
						Ask an organization OWNER to upgrade or manage the subscription.
					</Alert>
				)}

				{isOwner && (
					<Stack direction="row" flexWrap="wrap" gap={1.25}>
						{!isPro && (
							<Button
								variant="contained"
								disabled={busy}
								onClick={() => void startCheckout()}
							>
								{busy ? "Redirecting…" : "Upgrade to Professional"}
							</Button>
						)}
						{isPro && !usage?.entitlements.grandfathered && (
							<Button
								variant="outlined"
								disabled={busy}
								onClick={() => void openPortal()}
							>
								Manage subscription
							</Button>
						)}
						{usage?.entitlements.grandfathered && (
							<Alert severity="info" sx={{ flex: 1 }}>
								This organization is grandfathered for demo use and is not billed
								through Stripe.
							</Alert>
						)}
					</Stack>
				)}
			</GlassSurface>
		</Box>
	);
}
